import React, { useRef, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Galleria } from 'primereact/galleria';
import { logEvent } from '../../../../utils/analytics';
import DOMPurify from 'dompurify';
import './style.css';


export const ChatHistory = ({
    messages,
    senderName,
    showChatJourney,
    editingMessage,
    setEditingMessage,
    editMessage,
    isClosed,
    shopperName,
    isOwner,
    selectedTag,
    shopName,
    isBionicReading,
    isSending,
    // Journey-specific props
    currentJourneyNode,
    parentJourneyNode,
    chatJourney,
    onJourneyChoice,
    onJourneyBack,
    onTalkToShopOwner
}) => {
    const messagesEndRef = useRef(null);
    const [editText, setEditText] = useState(''); 

    const scrollToBottom = React.useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: isSending ? 'smooth' : 'auto' });
    }, [isSending]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSending, scrollToBottom]);



    const getSenderDisplayName = (msg) => {
        if (msg.type === 'bot') {
            return (shopName || "Shop");
        }
        if (msg.type === 'user') {
            return isOwner ? (msg.sender || "Inquirer") : (shopperName || "Inquirer");
        }
        if (msg.sender?.includes("Guest-")) {
            return isOwner ? (shopperName || "Inquirer") : (shopperName || "Inquirer");
        }
        return msg.sender || "Shop";
    };

    const handleEditSubmit = (msgId) => {
        if (editText.trim()) {
            logEvent('editMessage', { messageId: msgId, editText });
            editMessage(msgId, editText);
        }
    };

    const handleKeyPress = (e, msgId) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditSubmit(msgId);
        }
    };

    const applyBionicReading = (text) => {
        if (!isBionicReading || !text) return text;
        
        // Handle both string and HTML content
        const processText = (str) => {
            return str.split(/(<[^>]+>|\s+)/).map(part => {
                // Skip HTML tags
                if (part.startsWith('<') && part.endsWith('>')) {
                    return part;
                }
                // Process text content
                return part.split(/(\s+)/).map(word => {
                    if (word.trim().length <= 3 || !word.trim()) return word;
                    const boldLength = Math.ceil(word.trim().length * 0.4);
                    return word.trim().replace(
                        new RegExp(`^(.{${boldLength}})(.*)$`), 
                        '<b>$1</b>$2'
                    );
                }).join('');
            }).join('');
        };

        return processText(text);
    };

    const escapeHtml = (text) => {
        // Converts <, >, &, " and ' to their HTML entity equivalents
        return text.replace(/[&<>"']/g, (match) => {
            const entities = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            };
            return entities[match];
        });
    };

    const sanitizeText = (text) => {
        // Allow only <img> tags and text
        return DOMPurify.sanitize(text, { ALLOWED_TAGS: ['img'], ALLOWED_ATTR: ['src', 'style', 'alt'] });
    };

    // Utility to parse style string to React style object
    function parseStyleString(styleString) {
        if (!styleString) return undefined;
        return styleString.split(';').reduce((acc, rule) => {
            const [key, value] = rule.split(':').map(s => s && s.trim());
            if (key && value) {
                // Convert kebab-case to camelCase for React
                const camelKey = key.replace(/-([a-z])/g, g => g[1].toUpperCase());
                acc[camelKey] = value;
            }
            return acc;
        }, {});
    }

    // Galleria state and ref for each message


    // MessageContentWithGallery: per-message image gallery and preview
    // Now supports both legacy (images in HTML) and new (images array) messages
    const MessageContentWithGallery = ({ html, bionic, images }) => {
        const [galleriaActiveIndex, setGalleriaActiveIndex] = useState(0);
        const galleriaRef = useRef(null);
        const [brokenImages, setBrokenImages] = useState([]);
        // If images array is provided, use it for Galleria
        let imageParts = [];
        let textParts = [];
        if (images && Array.isArray(images) && images.length > 0) {
            // When both images and text: parse html for text only (ignore <img> tags)
            if (html) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const nodes = Array.from(tempDiv.childNodes);
                nodes.forEach((node, idx) => {
                    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
                        textParts.push(bionic ? (
                            <span key={idx} dangerouslySetInnerHTML={{ __html: applyBionicReading(escapeHtml(node.textContent)) }} />
                        ) : (
                            <span key={idx}>{node.textContent}</span>
                        ));
                    }
                });
            }
            imageParts = images.map((src, idx) => ({ src, alt: `image-${idx}` }));
        } else {
            // Legacy: Parse HTML string and separate text from images
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const nodes = Array.from(tempDiv.childNodes);
            nodes.forEach((node, idx) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    if (node.textContent.trim() !== '') {
                        textParts.push(bionic ? (
                            <span key={idx} dangerouslySetInnerHTML={{ __html: applyBionicReading(escapeHtml(node.textContent)) }} />
                        ) : (
                            <span key={idx}>{node.textContent}</span>
                        ));
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'img') {
                    // Extract image attributes
                    const src = node.getAttribute('src');
                    const style = node.getAttribute('style');
                    const alt = node.getAttribute('alt') || 'image';
                    imageParts.push({ src, style: parseStyleString(style), alt });
                }
            });
        }
        if (imageParts.length === 0) {
            return <>{textParts.length > 0 && <div>{textParts}</div>}</>;
        }
        // Galleria templates
        const itemTemplate = (item, index) => (
            brokenImages[index] ? (
                <div style={{
                    width: '100%',
                    minHeight: 120,
                    maxHeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f8d7da',
                    color: '#721c24',
                    borderRadius: 8,
                    fontWeight: 500,
                    fontSize: 16,
                    padding: 24
                }}>
                    Image unavailable — it may have been deleted or automatically removed after 30 days
                </div>
            ) : (
                <img
                    src={item.src}
                    alt={item.alt}
                    style={{ width: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 8, ...item.style }}
                    onError={() => setBrokenImages(prev => {
                        const arr = [...prev];
                        arr[index] = true;
                        return arr;
                    })}
                />
            )
        );
        const thumbnailTemplate = (item) => (
            <img
                src={item.src}
                alt={item.alt}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, ...item.style }}
            />
        );
        // Overlay for image count (only for multiple images)
        const showOverlay = imageParts.length > 1;
        const overlay = showOverlay ? (
            <div style={{
                position: 'absolute',
                bottom: 8,
                right: 12,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                padding: '2px 10px',
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 600,
                zIndex: 2
            }}>
                +{imageParts.length}
            </div>
        ) : null;
        // Only show the first image as preview if multiple images
        const preview = (
            <div
                style={{
                    position: 'relative',
                    marginTop: 8,
                    width: '100%',
                    minWidth: 120,
                    minHeight: 120,
                    maxHeight: 400,
                    height: showOverlay && !brokenImages[0] ? undefined : 'auto',
                    borderRadius: 8,
                    cursor: brokenImages[0] ? 'default' : 'pointer',
                    overflow: 'hidden',
                    aspectRatio: showOverlay && !brokenImages[0] ? '1/1' : undefined
                }}
                onClick={() => {
                    if (brokenImages[0]) return;
                    setGalleriaActiveIndex(0);
                    if (galleriaRef.current && galleriaRef.current.show) galleriaRef.current.show();
                }}
            >
                {brokenImages[0] ? (
                    <div style={{
                        width: '100%',
                        minHeight: 120,
                        maxHeight: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8d7da',
                        color: '#721c24',
                        borderRadius: 8,
                        fontWeight: 500,
                        fontSize: 16,
                        padding: 24
                    }}>
                        Image unavailable — it may have been deleted or automatically removed after 30 days
                    </div>
                ) : (
                    <img
                        src={imageParts[0].src}
                        alt={imageParts[0].alt}
                        style={{ width: '100%', height: '100%', objectFit: showOverlay ? 'cover' : 'contain', borderRadius: 8, minWidth: 120, minHeight: 120, maxWidth: 400, maxHeight: 400, ...imageParts[0].style }}
                        onError={() => setBrokenImages(prev => {
                            const arr = [...prev];
                            arr[0] = true;
                            return arr;
                        })}
                    />
                )}
                {!brokenImages[0] && showOverlay && overlay}
            </div>
        );
        return (
            <>
                {textParts.length > 0 && <div style={{ marginBottom: 8 }}>{textParts}</div>}
                {preview}
                <Galleria
                    ref={galleriaRef}
                    value={imageParts}
                    numVisible={9}
                    // style={{ maxWidth: '50%' }}
                    style={{ width: '850px' }}
                    circular
                    fullScreen
                    showItemNavigators={false}
                    showThumbnails={imageParts.length > 1 ? true : false}
                    item={itemTemplate}
                    thumbnail={thumbnailTemplate}
                    activeIndex={galleriaActiveIndex}
                    onItemChange={e => setGalleriaActiveIndex(e.index)}
                />
            </>
        );
    };

    const SytemBlocking = () => {
        return (

            selectedTag === "Block" && !isOwner && (
                <div className="system-message">
                    <div className="message-bubble system">
                        <div className="message-content">
                            <i className="pi pi-ban mr-2"></i>
                            You have been blocked from sending messages to this shop.
                        </div>
                    </div>
                </div>
            )
        )
    }

    // --- Normal Chat History ---
    return (
        <>
            <SytemBlocking />
            <div className="chat-messages-container-history">

                {messages
                    
                    .map((msg, index) => {
                    // Only skip if both text and images are missing
                    const hasText = !!msg.text && msg.text.trim() !== '';
                    const hasImages = Array.isArray(msg.images) && msg.images.length > 0;
                    if (!hasText && !hasImages) return null;

                    // For system journey path messages, override sender display
                    let sender = getSenderDisplayName(msg);
                    if (msg.type === 'system' && msg.meta && msg.meta.journeyPath && isOwner) {
                        // If sender is 'system - [name]', strip prefix for display
                        if (msg.sender && msg.sender.startsWith('system - ')) {
                            sender = msg.sender.replace(/^system - /, '');
                        }
                    }
                    // Determine ownership by role/type instead of display name to avoid collisions
                    const isCurrentUser = isOwner ? (msg.type === 'user') : (msg.type === 'guest');
                    const timeStamp = msg?.editedAt || msg.timestamp;
                    const messageTime = timeStamp?.toDate ?
                        timeStamp.toDate().toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        }) :
                        timeStamp?.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        }) || '';

                    const isLatestMessage = index === messages.length - 1;
                    const isEditing = editingMessage === msg.id;

                    return (
                        <div
                            key={index}
                            className={`message-wrapper ${
                                // For system journey path messages, alternate left/right by systemRole
                                msg.type === 'system' && msg.meta && msg.meta.journeyPath && isOwner
                                    ? (msg.meta.systemRole === 'bot' ? 'current-user' : 'system-client-bubble')
                                    : (isCurrentUser ? 'current-user' : '')
                            }`}
                        >
                            {msg?.buzzTimestamp !== undefined ? null :
  msg.id && isLatestMessage && isCurrentUser && !isClosed &&
  // Only show edit button if NOT a system message, or if owner
  ((msg.type !== 'system' || isOwner) && (
    <Button
      icon="pi pi-pencil"
      className="p-button-text p-button-sm edit-button"
      onClick={() => {
        setEditingMessage(msg.id);
        setEditText(msg.text);
      }}
      tooltip="Edit message"
      tooltipOptions={{ position: 'left' }}
    />
  ))
}
                            <div className={`message-bubble ${isCurrentUser ? 'current-user' : ''} ${isBionicReading ? 'bionic-reading' : ''}`}>
                                <div className="message-header">
                                    {isCurrentUser ? sender : msg.sender} {messageTime && `• ${messageTime}`}
                                    {msg.edited && <span className="edited-indicator">(edited)</span>}
                                </div>
                                {isEditing && isLatestMessage ? (
                                    <div className="edit-input-group">
                                        <InputText
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            autoFocus
                                            onKeyPress={(e) => handleKeyPress(e, msg.id)}
                                        />
                                        <Button
                                            icon="pi pi-check"
                                            onClick={() => handleEditSubmit(msg.id)}
                                            tooltip="Save changes"
                                            tooltipOptions={{ position: 'left' }}
                                            className="p-button p-button-outlined-white"
                                        />
                                        <Button
                                            icon="pi pi-times"
                                            onClick={() => setEditingMessage(null)}
                                            tooltip="Cancel edit"
                                            tooltipOptions={{ position: 'left' }}
                                            className="p-button p-button-outlined-white"
                                        />
                                    </div>
                                ) : (
                                    <div className="message-content">
                                        {(Array.isArray(msg.images) && msg.images.length > 0) ? (
                                    <MessageContentWithGallery
                                        html={sanitizeText(msg.text)}
                                        bionic={isBionicReading}
                                        images={msg.images}
                                    />
                                ) : (
                                    msg.text && (() => {
                                        let sanitized = sanitizeText(msg.text);
                                        sanitized = sanitized.replace(/^(<p><br><\/p>)+/gi, '').replace(/(<p><br><\/p>)+$/gi, '');
                                        if (sanitized.replace(/<[^>]+>/g, '').trim() === '') return null;
                                        return (
                                            <div
                                                className="message-html-content"
                                                dangerouslySetInnerHTML={{ 
                                                    __html: isBionicReading 
                                                        ? applyBionicReading(sanitized) 
                                                        : sanitized 
                                                }}
                                            />
                                        );
                                    })()
                                )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
        </>
    );
}
